import {
  COOKIE_KEY_ACCESS_TOKEN,
  COOKIE_KEY_MS_ACCESS_TOKEN,
  COOKIE_KEY_MS_REFRESH_TOKEN,
  COOKIE_KEY_REFRESH_TOKEN,
} from '@/constant/keys';
import { ApiTokenType, MSLoginToken, PortalToken } from '@/types/api';
import { Cookies } from 'react-cookie';
import dayjs from 'dayjs';
import { fetchApi } from '@/utils/api/customAxios';
import { twoWeek } from '@/constant';
import { PAGE_URL } from '@/constant/url';
import { isRnHost } from '@/utils/approval/isInApp';

const PATH = PAGE_URL.MAIN;

const useCookieDefaultOption = () => {
  const vercelEnv = process.env.VERCEL_ENV;
  const isProduction = vercelEnv === 'production';

  return {
    path: PATH, // 모든 경로에서 쿠키 접근 가능
    httpOnly: isProduction,
  };
};

/**
 * deleteCookie
 * - 클라이언트에서 설정한 쿠키를 제거합니다.
 * - 주의: 서버에서 HttpOnly로 설정된 쿠키는 클라이언트 JS로 삭제할 수 없습니다.
 */
export const deleteCookie = (
  name: string,
  opts?: {
    path?: string;
    domain?: string;
  },
) => {
  const cookie = new Cookies();

  // setCookie에서 path를 '/'로 고정했으므로 기본값도 '/'로
  const options: Record<string, any> = {
    path: opts?.path ?? PATH,
  };

  // 쿠키가 domain 옵션으로 설정된 경우 동일하게 넘겨줘야 제거됩니다.
  if (opts?.domain) options.domain = opts.domain;

  cookie.remove(name, options);
};

export const setCookie = (
  name: string,
  value: unknown,
  expires?: string | number,
) => {
  const cookie = new Cookies();
  const options = useCookieDefaultOption();
  let expiresOption: Record<string, any> = {};

  if (typeof expires === 'number') {
    // 초 단위 만료시간 (예: 3600 → 1시간)
    expiresOption = {
      expires: dayjs().add(expires, 'second').toDate(),
    };
  } else if (typeof expires === 'string') {
    // 문자열을 날짜로 변환 ('2025-12-31' 등)
    const parsed = dayjs(expires);
    if (parsed.isValid()) {
      expiresOption = { expires: parsed.toDate() };
    }
  }

  cookie.set(name, value, { ...options, ...expiresOption });
};

export const getCookie = <T>(name: string) => {
  const cookie = new Cookies();
  return cookie.get<T>(name);
};

export const getInitToken = async () => {
  const msRefreshToken = getCookie(COOKIE_KEY_MS_REFRESH_TOKEN);

  if (!msRefreshToken) window.location.href = PAGE_URL.LOGIN;

  await fetchApi<{ data: MSLoginToken }>({
    method: 'GET',
    url: `/api/auth/azure/token/refresh`,
    apiTokenType: ApiTokenType.MS,
    extraHeaders: {
      Authorization: `Bearer ${msRefreshToken}`,
    },
  }).then((res) => {
    const { access_token, refresh_token, expires_at } = res.data;
    setCookie(COOKIE_KEY_MS_ACCESS_TOKEN, access_token, expires_at); // FIXME: 이거 지금 서버에서 string으로 내려오고 있음
    setCookie(COOKIE_KEY_MS_REFRESH_TOKEN, refresh_token);
  });
  // getCookie();
  // console.log(cookies.getAll());
  // TODO: 여기서부터, access 토큰이랑 refresh 토큰 가지고 재발급 처리
  // TOKEN_KEY_SET.forEach((keyName) => {
  // const getByCookies = cookies.get<string | undefined>(keyName);
  // console.log(getByCookies);
  // setCookie(keyName, getByCookies);
  // });
};

export const setTokenInfo = (
  msTokenInfo: MSLoginToken,
  jwtTokenInfo: PortalToken,
) => {
  setCookie(
    COOKIE_KEY_MS_ACCESS_TOKEN,
    msTokenInfo.access_token,
    msTokenInfo.expires_at,
  );
  setCookie(COOKIE_KEY_MS_REFRESH_TOKEN, msTokenInfo.refresh_token, twoWeek);

  setCookie(
    COOKIE_KEY_ACCESS_TOKEN,
    jwtTokenInfo.data.access_token,
    jwtTokenInfo.data.expires_in,
  );
  setCookie(
    COOKIE_KEY_REFRESH_TOKEN,
    jwtTokenInfo.data.refresh_token,
    jwtTokenInfo.data.refresh_expires_in,
  );
};

export const removeCookie = (name: string) => deleteCookie(name);

export const setOauthToken = (url: string) => {
  return (
    url +
    (url.includes('?') ? '&oauthToken=' : '?oauthToken=') +
    getCookie(COOKIE_KEY_MS_ACCESS_TOKEN)
  );
};

export const setJwtToken = (url: string) => {
  return (
    url +
    (url.includes('?') ? '&portalToken=' : '?portalToken=') +
    getCookie(COOKIE_KEY_ACCESS_TOKEN)
  );
};

/**
 * 앱이 URL 로 넘긴 portalApiKey 를 액세스 토큰 쿠키로 옮김. setPortalKeyToken 이 보낸 것의 수신 측.
 * 자식 컴포넌트의 초기화 훅이 먼저 API 를 쏘기 때문에, effect 가 아니라 렌더 중 1회 호출.
 */
export const hydrateTokenFromQuery = () => {
  if (typeof window === 'undefined') return;
  // 앱 웹뷰에서만 허용. 일반 브라우저까지 열어두면 남의 JWT 를 붙인 링크 하나로 세션이 바뀜
  if (!isRnHost()) return;

  const params = new URLSearchParams(window.location.search);
  const portalApiKey = params.get('portalApiKey');
  if (!portalApiKey) return;

  setCookie(COOKIE_KEY_ACCESS_TOKEN, portalApiKey);

  // 쿠키로 옮겼으면 URL 에서 지움. 안 지우면 히스토리·Referer·Hotjar 기록에 JWT 가 그대로 남음
  params.delete('portalApiKey');
  const query = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${
      window.location.hash
    }`,
  );
};

export const setPortalKeyToken = (url: string) => {
  return (
    url +
    (url.includes('?') ? '&portalApiKey=' : '?portalApiKey=') +
    encodeURIComponent(getCookie(COOKIE_KEY_ACCESS_TOKEN))
  );
};
